import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { API } from '@/utils/constants';

/**
 * Global Search Component
 * Opens with Ctrl+K keyboard shortcut or by clicking the search bar
 * Searches across jobs, customers, and suppliers
 */
export const GlobalSearch = () => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState({ jobs: [], customers: [], suppliers: [] });
  const [loading, setLoading] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const navigate = useNavigate();

  // Flatten results for keyboard navigation
  const flatResults = [
    ...results.jobs.map(j => ({ type: 'job', data: j })),
    ...results.customers.map(c => ({ type: 'customer', data: c })),
    ...results.suppliers.map(s => ({ type: 'supplier', data: s }))
  ];

  // Keyboard shortcut handler
  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.ctrlKey || e.metaCmd) && e.key === 'k') {
        e.preventDefault();
        setOpen(true);
      }
      if (e.key === 'Escape') {
        setOpen(false);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, []);

  // Search debounce
  const search = useCallback(async (searchQuery) => {
    if (!searchQuery || searchQuery.length < 2) {
      setResults({ jobs: [], customers: [], suppliers: [] });
      return;
    }

    setLoading(true);
    try {
      const response = await axios.get(`${API}/global-search?q=${encodeURIComponent(searchQuery)}`);
      setResults(response.data);
      setSelectedIndex(0);
    } catch (error) {
      console.error('Search error:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  // Debounced search (150ms feels snappy without flooding the API)
  useEffect(() => {
    const timer = setTimeout(() => {
      search(query);
    }, 150);
    return () => clearTimeout(timer);
  }, [query, search]);

  // Handle keyboard navigation
  const handleKeyDown = (e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedIndex(prev => Math.min(prev + 1, flatResults.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedIndex(prev => Math.max(prev - 1, 0));
    } else if (e.key === 'Enter' && flatResults[selectedIndex]) {
      e.preventDefault();
      handleSelect(flatResults[selectedIndex]);
    }
  };

  // Handle selection
  const handleSelect = (item) => {
    setOpen(false);
    setQuery('');
    setResults({ jobs: [], customers: [], suppliers: [] });

    if (item.type === 'job') {
      navigate(`/job/${item.data.id}`);
    } else if (item.type === 'customer') {
      navigate(`/customers?highlight=${item.data.id}`);
    } else if (item.type === 'supplier') {
      navigate(`/suppliers?highlight=${item.data.id}`);
    }
  };

  // Reset on close
  const handleOpenChange = (isOpen) => {
    setOpen(isOpen);
    if (!isOpen) {
      setQuery('');
      setResults({ jobs: [], customers: [], suppliers: [] });
      setSelectedIndex(0);
    }
  };

  return (
    <>
      {/* Search Bar Trigger - Fixed at top */}
      <div className="global-search-bar" style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        zIndex: 100,
        backgroundColor: '#f8fafc',
        borderBottom: '1px solid #e2e8f0',
        padding: '0.5rem 1rem',
        display: 'flex',
        justifyContent: 'center'
      }}>
        <button
          onClick={() => setOpen(true)}
          data-testid="global-search-trigger"
          className="global-search-btn"
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.75rem',
            padding: '0.5rem 1rem',
            backgroundColor: 'white',
            border: '1px solid #d1d5db',
            borderRadius: '0.5rem',
            cursor: 'pointer',
            width: '100%',
            maxWidth: '500px',
            color: '#6b7280',
            fontSize: '0.875rem',
            transition: 'all 0.15s ease'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.borderColor = '#3b82f6';
            e.currentTarget.style.boxShadow = '0 0 0 2px rgba(59, 130, 246, 0.1)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.borderColor = '#d1d5db';
            e.currentTarget.style.boxShadow = 'none';
          }}
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="11" cy="11" r="8"></circle>
            <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
          </svg>
          <span style={{ flex: 1, textAlign: 'left' }}>Search jobs, customers, suppliers...</span>
          <span style={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.25rem',
            padding: '0.25rem 0.5rem',
            backgroundColor: '#3b82f6',
            color: 'white',
            borderRadius: '0.25rem',
            fontSize: '0.75rem',
            fontWeight: '500'
          }}>
            Ctrl K
          </span>
        </button>
      </div>

      {/* Search Dialog */}
      <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-[550px] p-0" style={{ maxHeight: '80vh' }}>
        <DialogHeader className="px-4 pt-4 pb-0">
          <DialogTitle className="flex items-center gap-2">
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="11" cy="11" r="8"></circle>
              <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
            </svg>
            Global Search
          </DialogTitle>
        </DialogHeader>
        
        <div className="px-4 py-2">
          <Input
            placeholder="Search jobs, customers, suppliers... (min 2 characters)"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={handleKeyDown}
            autoFocus
            data-testid="global-search-input"
          />
          <div className="text-xs text-gray-400 mt-1">
            Press <kbd className="px-1 py-0.5 bg-gray-100 rounded text-gray-600">↑</kbd> <kbd className="px-1 py-0.5 bg-gray-100 rounded text-gray-600">↓</kbd> to navigate, <kbd className="px-1 py-0.5 bg-gray-100 rounded text-gray-600">Enter</kbd> to select
          </div>
        </div>

        <div className="overflow-y-auto" style={{ maxHeight: '50vh' }}>
          {loading && (
            <div className="px-4 py-8 text-center text-gray-500">
              Searching...
            </div>
          )}

          {!loading && query.length >= 2 && flatResults.length === 0 && (
            <div className="px-4 py-8 text-center text-gray-500">
              No results found for "{query}"
            </div>
          )}

          {!loading && results.jobs.length > 0 && (
            <div className="border-t">
              <div className="px-4 py-2 bg-gray-50 text-xs font-semibold text-gray-500 uppercase tracking-wide">
                Jobs ({results.jobs.length})
              </div>
              {results.jobs.map((job, idx) => {
                const globalIdx = idx;
                return (
                  <div
                    key={job.id}
                    className={`px-4 py-3 cursor-pointer border-b last:border-b-0 ${selectedIndex === globalIdx ? 'bg-blue-50' : 'hover:bg-gray-50'}`}
                    onClick={() => handleSelect({ type: 'job', data: job })}
                    data-testid={`search-result-job-${job.id}`}
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <span className="font-semibold text-blue-600">#{job.job_number}</span>
                        <span className="mx-2">—</span>
                        <span className="font-medium">{job.customer_name}</span>
                      </div>
                      <span className={`text-xs px-2 py-1 rounded ${
                        job.status === 'Completed' ? 'bg-green-100 text-green-700' :
                        job.status === 'In Progress' ? 'bg-blue-100 text-blue-700' :
                        'bg-gray-100 text-gray-700'
                      }`}>
                        {job.status}
                      </span>
                    </div>
                    <div className="text-sm text-gray-500 mt-1">
                      {job.brand && <span>{job.brand}</span>}
                      {job.model_number && <span> • {job.model_number}</span>}
                      {job.mobile && <span> • {job.mobile}</span>}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {!loading && results.customers.length > 0 && (
            <div className="border-t">
              <div className="px-4 py-2 bg-gray-50 text-xs font-semibold text-gray-500 uppercase tracking-wide">
                Customers ({results.customers.length})
              </div>
              {results.customers.map((customer, idx) => {
                const globalIdx = results.jobs.length + idx;
                return (
                  <div
                    key={customer.id}
                    className={`px-4 py-3 cursor-pointer border-b last:border-b-0 ${selectedIndex === globalIdx ? 'bg-blue-50' : 'hover:bg-gray-50'}`}
                    onClick={() => handleSelect({ type: 'customer', data: customer })}
                    data-testid={`search-result-customer-${customer.id}`}
                  >
                    <div className="font-medium">{customer.name}</div>
                    <div className="text-sm text-gray-500">
                      {customer.email && <span>{customer.email}</span>}
                      {customer.phone && <span> • {customer.phone}</span>}
                      {customer.company && <span> • {customer.company}</span>}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {!loading && results.suppliers.length > 0 && (
            <div className="border-t">
              <div className="px-4 py-2 bg-gray-50 text-xs font-semibold text-gray-500 uppercase tracking-wide">
                Suppliers ({results.suppliers.length})
              </div>
              {results.suppliers.map((supplier, idx) => {
                const globalIdx = results.jobs.length + results.customers.length + idx;
                return (
                  <div
                    key={supplier.id}
                    className={`px-4 py-3 cursor-pointer border-b last:border-b-0 ${selectedIndex === globalIdx ? 'bg-blue-50' : 'hover:bg-gray-50'}`}
                    onClick={() => handleSelect({ type: 'supplier', data: supplier })}
                    data-testid={`search-result-supplier-${supplier.id}`}
                  >
                    <div className="font-medium">{supplier.name}</div>
                    <div className="text-sm text-gray-500">
                      {supplier.contact_person && <span>{supplier.contact_person}</span>}
                      {supplier.email && <span> • {supplier.email}</span>}
                      {supplier.phone && <span> • {supplier.phone}</span>}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div className="px-4 py-2 border-t bg-gray-50 text-xs text-gray-500">
          <span className="mr-4">Press <kbd className="px-1 py-0.5 bg-white border rounded">Ctrl</kbd> + <kbd className="px-1 py-0.5 bg-white border rounded">K</kbd> to open search anytime</span>
        </div>
      </DialogContent>
    </Dialog>
    </>
  );
};

export default GlobalSearch;
